// Resolve an activity actor id to a cybo avatar (URL or emoji), mirroring
// AuthorAvatar's roster mapping: the id is either the cybo id itself, or a
// spawned session's agentId that the workspace agent roster maps back to its
// cybo. Returns null for humans/unknown ids so callers fall through to the
// human image path (getMemberImage → initials).
//
// Id-driven on purpose: server-seeded activity rows stamp actorType "human"
// for EVERY actor (serverItemToActivity — the row doesn't store actor type),
// so trusting actorType left agent rows on letter fallbacks. A cybo-roster
// hit is authoritative regardless of the stamped type.
//
// Pure + DI (no store imports) so it unit-tests without mocking $state.
import { presetAvatarForCyboId } from "$lib/utils.js";
import { sessionStampProvider } from "$lib/agent-display.js";
import { reportUiFailure, shouldReport } from "$lib/report-failure.js";
import type { ChatIdentityAgentRow } from "$lib/cybo-chat-identity.js";

// The avatar string a cybo renders on EVERY surface: its own avatar (image URL
// or emoji) when set, else its deterministic preset portrait. Never null — a
// cybo ALWAYS has a face, so no surface should fall back to a name initial.
// This is the ONE rule; AuthorAvatar, the thread-reply indicator, and
// resolveAgentAvatar below all route through it.
export function cyboAvatarFor(cybo: { id: string; avatar?: string | null }): string {
  return cybo.avatar || presetAvatarForCyboId(cybo.id);
}

// AGENT author missing from the roster: its cybo by NAME, else the id's preset (the facepile's
// face) unless a cybo shares the name (the id may be its session: initials, not a wrong face).
export function missingCyboAvatarFor(
  id: string,
  name: string | null | undefined,
  cybos: ReadonlyArray<{ id: string; name: string; avatar?: string | null }> | undefined,
): string | null {
  const byName = resolveCyboByNameOrId(name, cybos);
  if (byName) return cyboAvatarFor(byName);
  return cybos?.some((c) => c.name === name) ? null : presetAvatarForCyboId(id); // null: ambiguous name, initials
}

export function reportAgentAvatarGuessed(
  surface: string,
  agentId: string,
  outcome: "preset" | "initials",
): void {
  if (!shouldReport(`agent_avatar_guessed:${agentId}`, Number.POSITIVE_INFINITY)) return;
  reportUiFailure(
    "ui.agent_avatar_guessed",
    new Error("agent avatar guessed"),
    { surface, agentId, outcome },
    { failureKind: "refusal", code: "not_found" },
  );
}

// Resolve a label that is EITHER a cybo id OR a display name (thread
// participants carry only a name + image — no id — so id-only matching leaves
// cybos on letter fallbacks) to its cybo row: id match first, then name.
// Returns undefined for humans/unknowns/ambiguous names so callers keep the human image path.
export function resolveCyboByNameOrId<C extends { id: string; name: string }>(
  nameOrId: string | null | undefined,
  cybos: ReadonlyArray<C> | undefined,
): C | undefined {
  if (!nameOrId || !cybos) return undefined;
  const byId = cybos.find((c) => c.id === nameOrId);
  if (byId) return byId;
  // Two cybos sharing a name: a name cannot say which one, so no face beats the wrong face.
  const byName = cybos.filter((c) => c.name === nameOrId);
  return byName.length === 1 ? byName[0] : undefined;
}

export function resolveAgentAvatar(
  actorId: string | null | undefined,
  cybos: ReadonlyArray<{ id: string; avatar?: string | null }> | undefined,
  agents: ReadonlyArray<{ agentId: string; cyboId?: string | null }> | undefined,
): string | null {
  if (!actorId) return null;
  let cybo = cybos?.find((c) => c.id === actorId);
  if (!cybo) {
    const cyboId = agents?.find((a) => a.agentId === actorId)?.cyboId;
    cybo = cyboId ? cybos?.find((c) => c.id === cyboId) : undefined;
  }
  if (!cybo) return null;
  // Custom avatar (image URL or emoji — Avatar's resolveAvatarSource handles
  // both) → else the cybo's deterministic preset portrait, same fallback
  // AuthorAvatar uses so an agent looks identical here and in the channel.
  return cyboAvatarFor(cybo);
}

// A face for a card that names someone but carries no id (a task card's assignee): the one
// member, else the one agent, with that exact name. Two with the same name get initials.
export function faceByName(
  name: string,
  members: ReadonlyArray<{ userId: string; name: string | null }> | undefined,
  cybos: ReadonlyArray<{ id: string; name: string; avatar?: string | null }> | undefined,
  memberImage: (userId: string) => string | null,
): string | null {
  const people = (members ?? []).filter((m) => m.name === name);
  if (people.length === 1 && people[0]) return memberImage(people[0].userId);
  const agents = (cybos ?? []).filter((c) => c.name === name);
  return people.length === 0 && agents.length === 1 && agents[0] ? cyboAvatarFor(agents[0]) : null; // null: initials
}

// The faces a reply facepile shows: never a system row's "System" author (an older relay or
// the local cache still lists it), at most `max`.
export function facepileParticipants<P extends { id?: string }>(list: readonly P[], max = 3): P[] {
  return list.filter((p) => p.id !== "system").slice(0, max);
}

// A reply-facepile participant's face: by id (an agent id is a cybo id), name only for an
// older relay that sends no id.
export function threadParticipantAvatar(
  p: { id?: string; kind?: "human" | "agent"; name: string; image?: string | null },
  cybos: ReadonlyArray<{ id: string; name: string; avatar?: string | null }> | undefined,
  agents: ReadonlyArray<{ agentId: string; cyboId?: string | null }> | undefined,
  // A human's live face, the one a message author shows (authState.getMemberImage).
  memberImage?: (userId: string) => string | null,
): string | null {
  if (p.id) {
    const face = resolveAgentAvatar(p.id, cybos, agents);
    if (face) return face;
    if (p.kind === "agent") return cyboAvatarFor({ id: p.id, avatar: p.image });
    return memberImage?.(p.id) ?? p.image ?? null; // null: no image, initials
  }
  const cybo = resolveCyboByNameOrId(p.name, cybos);
  return cybo ? cyboAvatarFor(cybo) : (p.image ?? null); // null: no image, initials
}

// A generic session's provider: the roster row's first (a session NAMED "Codex"
// may run Claude), else the stamped label in the name ("Claude", "Ana's Claude" → claude).
export function sessionProviderFor(
  agentId: string | null | undefined,
  name: string | null | undefined,
  agents: ReadonlyArray<{ agentId: string; provider?: string | null }> | undefined,
): string | undefined {
  const row = agentId ? agents?.find((a) => a.agentId === agentId) : undefined;
  return row?.provider ?? sessionStampProvider(name) ?? undefined;
}

// A roster row as CyboSessionAvatar (the Chats sidebar glyph) takes it.
export interface SessionAvatarAgent extends ChatIdentityAgentRow {
  agentId?: string | null;
}

// cybo → its face; session → CyboSessionAvatar(agent), the sidebar's glyph;
// person → the human path (member photo → initials).
export type ActorVisual =
  | { kind: "cybo"; avatar: string }
  | { kind: "session"; agent: SessionAvatarAgent }
  | { kind: "person" };

export function resolveActorVisual(opts: {
  actorId: string | null | undefined;
  actorName: string | null | undefined;
  cybos: ReadonlyArray<{ id: string; name?: string; avatar?: string | null }> | undefined;
  agents: ReadonlyArray<SessionAvatarAgent & { agentId: string }> | undefined;
  isMember?: (id: string) => boolean;
  /** Caller knows the actor is an agent (e.g. message fromType) — a roster miss then
   *  gets its id's preset face unless a cybo shares its name (missingCyboAvatarFor). */
  isAgent?: boolean;
}): ActorVisual {
  const { actorId, actorName, cybos, agents, isMember, isAgent } = opts;
  const cyboAvatar = resolveAgentAvatar(actorId, cybos, agents);
  if (cyboAvatar) return { kind: "cybo", avatar: cyboAvatar };
  // The roster row itself, so the provider is the roster's, never the name's.
  const row = actorId ? agents?.find((a) => a.agentId === actorId) : undefined;
  if (row) return { kind: "session", agent: row };
  // Name fallback: a member (or cybo) named "Claude" is not a Claude session.
  if (actorId && isMember?.(actorId)) return { kind: "person" };
  const provider = sessionStampProvider(actorName);
  if (isAgent && actorId && !provider) {
    const named = cybos?.filter((c): c is typeof c & { name: string } => !!c.name);
    const face = missingCyboAvatarFor(actorId, actorName, named);
    if (face) return { kind: "cybo", avatar: face };
  }
  if (!provider || cybos?.some((c) => c.name === actorName)) return { kind: "person" };
  return { kind: "session", agent: { agentId: actorId, provider } };
}
